<template>
  <div class="finance-reports" v-loading="loading">
    <!-- Header -->
    <div class="fr-header">
      <div class="fr-header__titles">
        <h2 class="fr-title">Mahalliy byudjet</h2>
        <p class="fr-subtitle">Viloyat va tuman byudjet ijrosi — yil va chorak kesimida</p>
      </div>
      <div class="fr-actions">
        <el-button type="primary" @click="openCreate">
          <el-icon><Plus /></el-icon><span>Kiritish</span>
        </el-button>
        <el-button @click="uploadVisible = true">
          <el-icon><Upload /></el-icon><span>Excel yuklash</span>
        </el-button>
        <el-button :loading="exporting" @click="onExport">
          <el-icon><Download /></el-icon><span>Eksport</span>
        </el-button>
      </div>
    </div>

    <!-- Toolbar / filters -->
    <div class="fr-toolbar">
      <div class="fr-toolbar__group">
        <span class="fr-toolbar__label">Hudud</span>
        <el-radio-group v-model="entity">
          <el-radio-button label="region">Viloyatlar</el-radio-button>
          <el-radio-button label="district">Tumanlar</el-radio-button>
        </el-radio-group>
      </div>

      <span class="fr-toolbar__divider" />

      <div class="fr-toolbar__group">
        <span class="fr-toolbar__label">Ko'rinish</span>
        <el-radio-group v-model="viewMode">
          <el-radio-button label="period">Davr bo'yicha</el-radio-button>
          <el-radio-button label="history">To'liq tarix</el-radio-button>
        </el-radio-group>
      </div>

      <div class="fr-toolbar__group" v-if="viewMode === 'period'">
        <span class="fr-toolbar__label">Davr</span>
        <el-select v-model="year" class="fr-year" placeholder="Yil">
          <el-option v-for="y in years" :key="y" :value="y" :label="y" />
        </el-select>
        <el-select v-model="quarter" class="fr-quarter" placeholder="Chorak">
          <el-option v-for="q in 4" :key="q" :value="q" :label="`${q}-chorak`" />
          <el-option :value="null" label="Yillik (barcha choraklar)" />
        </el-select>
      </div>

      <div class="fr-toolbar__group" v-if="entity === 'district'">
        <span class="fr-toolbar__label">Viloyat</span>
        <el-select
          v-model="regionFilter"
          class="fr-region"
          placeholder="Barcha viloyatlar"
          clearable
          filterable
        >
          <el-option
            v-for="region in regions"
            :key="region.id"
            :value="region.id"
            :label="region.title"
          />
        </el-select>
      </div>

      <span class="fr-count" v-if="!loading && !error">
        {{ entity === "region" ? "Viloyatlar" : "Tumanlar" }}: <b>{{ displayRows.length }}</b>
      </span>
    </div>

    <!-- Summary stat cards -->
    <div class="fr-stats" v-if="!error && displayRows.length">
      <div class="fr-stat fr-stat--income">
        <div class="fr-stat__head"><span class="fr-stat__dot" />Umumiy kirim</div>
        <div class="fr-stat__value">{{ compactAmount(totals.income) }}</div>
        <div class="fr-stat__full">{{ formatAmount(totals.income) }} so'm</div>
      </div>
      <div class="fr-stat fr-stat--outcome">
        <div class="fr-stat__head"><span class="fr-stat__dot" />Umumiy chiqim</div>
        <div class="fr-stat__value">{{ compactAmount(totals.outcome) }}</div>
        <div class="fr-stat__full">{{ formatAmount(totals.outcome) }} so'm</div>
      </div>
      <div class="fr-stat fr-stat--transfer">
        <div class="fr-stat__head"><span class="fr-stat__dot" />Umumiy transfer</div>
        <div class="fr-stat__value">{{ compactAmount(totals.transfer) }}</div>
        <div class="fr-stat__full">{{ formatAmount(totals.transfer) }} so'm</div>
      </div>
    </div>

    <!-- Error -->
    <el-alert
      v-if="error"
      type="error"
      show-icon
      :closable="false"
      class="fr-error"
    >
      <template #default>
        <div class="fr-error__body">
          <span>{{ error }}</span>
          <el-button size="small" type="primary" @click="fetchRows">Qayta urinish</el-button>
        </div>
      </template>
    </el-alert>

    <!-- Table -->
    <el-table
      v-else
      :data="displayRows"
      border
      stripe
      class="fr-table"
      max-height="calc(100vh - 340px)"
      show-summary
      sum-text="Jami"
      :summary-method="getSummaries"
      row-key="rowKey"
    >
      <el-table-column type="index" label="T/R" width="64" align="center" fixed="left" />

      <el-table-column
        v-if="entity === 'district' && !regionFilter"
        prop="region_title"
        label="Viloyat"
        min-width="170"
        fixed="left"
      />
      <el-table-column
        :prop="entity === 'region' ? 'region_title' : 'district_title'"
        :label="entity === 'region' ? 'Viloyat' : 'Tuman'"
        min-width="190"
        fixed="left"
      >
        <template #default="scope">
          <span
            class="fr-hudud"
            :class="{ 'fr-hudud--nested': entity === 'district' && !regionFilter }"
          >
            {{ entity === "region" ? scope.row.region_title : scope.row.district_title }}
          </span>
        </template>
      </el-table-column>

      <el-table-column v-if="showQuarterColumns" prop="year" label="Yil" width="90" align="center" />
      <el-table-column v-if="showQuarterColumns" label="Chorak" width="110" align="center">
        <template #default="scope">
          <el-tag :type="quarterTagType(scope.row.quarter)" size="small" effect="light" round>
            {{ scope.row.quarter }}-chorak
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="income" min-width="160" align="right">
        <template #header>
          <span class="fr-colhead"><i class="fr-dot fr-dot--income" />Kirim <small>(so'm)</small></span>
        </template>
        <template #default="scope">
          <el-tooltip :content="compactAmount(scope.row.income)" placement="top" :show-after="200">
            <span class="fr-amount">{{ formatAmount(scope.row.income) }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="outcome" min-width="160" align="right">
        <template #header>
          <span class="fr-colhead"><i class="fr-dot fr-dot--outcome" />Chiqim <small>(so'm)</small></span>
        </template>
        <template #default="scope">
          <el-tooltip :content="compactAmount(scope.row.outcome)" placement="top" :show-after="200">
            <span class="fr-amount">{{ formatAmount(scope.row.outcome) }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="transfer" min-width="160" align="right">
        <template #header>
          <span class="fr-colhead"><i class="fr-dot fr-dot--transfer" />Transfer <small>(so'm)</small></span>
        </template>
        <template #default="scope">
          <el-tooltip :content="compactAmount(scope.row.transfer)" placement="top" :show-after="200">
            <span class="fr-amount">{{ formatAmount(scope.row.transfer) }}</span>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column v-if="canEditRows" label="Amallar" width="110" align="center" fixed="right">
        <template #default="scope">
          <div class="fr-row-actions" v-if="isEditable(scope.row)">
            <el-tooltip content="Tahrirlash" placement="top">
              <el-button type="primary" circle size="small" @click="openEdit(scope.row)">
                <img alt="edit" class="fr-act-icon" src="@/assets/images/icons/edit.svg" />
              </el-button>
            </el-tooltip>
            <el-tooltip content="O'chirish" placement="top">
              <el-button type="danger" circle size="small" @click="onDelete(scope.row)">
                <img alt="delete" class="fr-act-icon" src="@/assets/images/icons/delete.svg" />
              </el-button>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>

      <template #empty>
        <el-empty description="Bu davr uchun ma'lumot yo'q">
          <el-button type="primary" @click="openCreate">
            <el-icon><Plus /></el-icon><span>Ma'lumot kiritish</span>
          </el-button>
        </el-empty>
      </template>
    </el-table>

    <finance-report-form-modal
      v-model="formVisible"
      :mode="formMode"
      :row="formRow"
      :defaults="formDefaults"
      @saved="fetchRows"
    />
    <finance-report-upload-modal
      v-model="uploadVisible"
      :default-year="year"
      :default-quarter="quarter || 1"
      @uploaded="fetchRows"
    />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import { Plus, Upload, Download } from "@element-plus/icons-vue";
import { formatterPrice, blobErrorMessage } from "@/utils/common";
import api from "./financeReportsApi";
import FinanceReportFormModal from "./FinanceReportFormModal.vue";
import FinanceReportUploadModal from "./FinanceReportUploadModal.vue";

function currentQuarter() {
  return Math.floor(new Date().getMonth() / 3) + 1;
}

export default {
  name: "FinanceReports",
  components: {
    Plus,
    Upload,
    Download,
    FinanceReportFormModal,
    FinanceReportUploadModal,
  },
  data() {
    return {
      entity: "region", // "region" | "district"
      viewMode: "period", // "period" | "history"
      year: new Date().getFullYear(),
      quarter: currentQuarter(), // number = exact quarter, null = annual
      regionFilter: null,
      regions: [],
      rows: [],
      loading: false,
      exporting: false,
      error: null,
      formVisible: false,
      formMode: "create",
      formRow: null,
      uploadVisible: false,
    };
  },
  computed: {
    years() {
      const current = new Date().getFullYear();
      const list = [];
      for (let y = current + 1; y >= 2020; y--) list.push(y);
      return list;
    },
    // Annual = year set, quarter omitted (backend returns id:0 / quarter:null).
    isAnnual() {
      return this.viewMode === "period" && this.quarter === null;
    },
    // Full-history stacks many quarters → show Year/Quarter columns.
    showQuarterColumns() {
      return this.viewMode === "history";
    },
    // Annual aggregate rows are not real rows — no edit/delete (§2.4.1).
    canEditRows() {
      return !this.isAnnual;
    },
    queryParams() {
      const params = {};
      if (this.viewMode === "period") {
        params.year = this.year;
        if (this.quarter !== null) params.quarter = this.quarter;
      }
      if (this.entity === "district" && this.regionFilter) {
        params.region_id = this.regionFilter;
      }
      return params;
    },
    // Re-fetch whenever any of these change.
    fetchKey() {
      return `${this.entity}|${this.viewMode}|${this.year}|${this.quarter}|${this.regionFilter}`;
    },
    displayRows() {
      const rows = this.rows.map((r, i) => ({
        ...r,
        // annual rows all share id:0 — build a stable key for row-key/summary.
        rowKey: `${r.id}-${r.region_id}-${r.district_id ?? ""}-${r.year}-${r.quarter}-${i}`,
      }));
      // Sort for stable region→district grouping (backend already orders, this
      // keeps it consistent across modes).
      return rows.sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) return (a.year || 0) - (b.year || 0);
        if ((a.quarter || 0) !== (b.quarter || 0)) return (a.quarter || 0) - (b.quarter || 0);
        if (a.region_id !== b.region_id) return a.region_id - b.region_id;
        return (a.district_id || 0) - (b.district_id || 0);
      });
    },
    // Totals of the currently visible rows (drives the stat cards; mirrors the
    // table's summary footer).
    totals() {
      return this.displayRows.reduce(
        (acc, r) => {
          acc.income += Number(r.income) || 0;
          acc.outcome += Number(r.outcome) || 0;
          acc.transfer += Number(r.transfer) || 0;
          return acc;
        },
        { income: 0, outcome: 0, transfer: 0 }
      );
    },
    formDefaults() {
      return {
        entity: this.entity,
        year: this.viewMode === "period" ? this.year : new Date().getFullYear(),
        quarter: this.viewMode === "period" && this.quarter ? this.quarter : currentQuarter(),
      };
    },
  },
  watch: {
    fetchKey() {
      this.fetchRows();
    },
  },
  mounted() {
    this.$store.commit("updateHeaderTitle", "Mahalliy byudjet");
    this.loadRegions();
    this.fetchRows();
  },
  methods: {
    formatAmount(value) {
      if (value === null || value === undefined) return "—";
      return formatterPrice(value);
    },
    // Compact tooltip for the large regional figures (§2.7).
    compactAmount(value) {
      if (value === null || value === undefined) return "";
      const abs = Math.abs(value);
      if (abs >= 1e12) return `${(value / 1e12).toFixed(1)} trln so'm`;
      if (abs >= 1e9) return `${(value / 1e9).toFixed(1)} mlrd so'm`;
      if (abs >= 1e6) return `${(value / 1e6).toFixed(1)} mln so'm`;
      return `${formatterPrice(value)} so'm`;
    },
    isEditable(row) {
      // Real rows have a non-null quarter and a truthy id; annual rows don't.
      return row.quarter !== null && row.quarter !== undefined && !!row.id;
    },
    // Distinct colour per quarter for the full-history Chorak chips.
    quarterTagType(quarter) {
      return { 1: "primary", 2: "success", 3: "warning", 4: "danger" }[quarter] || "info";
    },
    getSummaries({ columns, data }) {
      return columns.map((col, index) => {
        if (index === 0) return "Jami";
        const prop = col.property;
        if (prop === "income" || prop === "outcome" || prop === "transfer") {
          const sum = data.reduce((acc, row) => acc + (Number(row[prop]) || 0), 0);
          return formatterPrice(sum);
        }
        return "";
      });
    },
    loadRegions() {
      api
        .listRegionPicker()
        .then(({ data }) => {
          this.regions = data.regions || [];
        })
        .catch(() => ElMessage.error("Viloyatlar ro'yxatini yuklashda xatolik"));
    },
    fetchRows() {
      this.loading = true;
      this.error = null;
      const params = this.queryParams;
      const request =
        this.entity === "region" ? api.listRegions(params) : api.listDistricts(params);
      request
        .then(({ data }) => {
          this.rows =
            this.entity === "region"
              ? data.region_finance_reports || []
              : data.district_finance_reports || [];
        })
        .catch(({ response }) => {
          this.rows = [];
          this.error =
            response?.data?.message || "Ma'lumotni yuklashda xatolik yuz berdi";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.formMode = "create";
      this.formRow = null;
      this.formVisible = true;
    },
    openEdit(row) {
      this.formMode = "edit";
      this.formRow = {
        entity: this.entity,
        id: row.id,
        region_id: row.region_id,
        district_id: row.district_id,
        region_title: row.region_title,
        district_title: row.district_title,
        year: row.year,
        quarter: row.quarter,
        income: row.income,
        outcome: row.outcome,
        transfer: row.transfer,
      };
      this.formVisible = true;
    },
    onDelete(row) {
      ElMessageBox.confirm(
        "Ushbu chorak ma'lumotini o'chirmoqchimisiz?",
        "Diqqat!",
        {
          confirmButtonText: "Ha, o'chirilsin",
          cancelButtonText: "Bekor qilish",
          type: "warning",
        }
      )
        .then(() => {
          this.loading = true;
          const request =
            this.entity === "region"
              ? api.deleteRegion(row.id)
              : api.deleteDistrict(row.id);
          return request
            .then(() => {
              ElMessage.success("Ma'lumot o'chirildi");
              this.fetchRows();
            })
            .catch(({ response }) => {
              this.loading = false;
              ElNotification({
                title: "Xatolik",
                message: response?.data?.message || "O'chirishda xatolik yuz berdi",
                type: "error",
              });
            });
        })
        .catch(() => {
          /* dialog cancelled */
        });
    },
    onExport() {
      this.exporting = true;
      api
        .exportExcel(this.queryParams)
        .then((res) => {
          const url = URL.createObjectURL(
            new Blob([res.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
            })
          );
          const h = res.headers || {};
          const disposition =
            (typeof h.get === "function" ? h.get("content-disposition") : h["content-disposition"]) || "";
          const match = disposition.match(/filename=([^;]+)/);
          const fileName = match ? match[1].trim().replace(/"/g, "") : "finance-report.xlsx";
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch(async ({ response }) => {
          const message = await blobErrorMessage(response, "Eksport qilishda xatolik yuz berdi");
          ElNotification({ title: "Xatolik", message, type: "error" });
        })
        .finally(() => {
          this.exporting = false;
        });
    },
  },
};
</script>

<style scoped>
.finance-reports {
  --fr-income: #21a366;
  --fr-outcome: #e08e0b;
  --fr-transfer: #3a7afe;
}

/* ---- Header ---- */
.fr-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
}
.fr-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1f2d3d;
}
.fr-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #909399;
}
.fr-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.fr-actions :deep(.el-button) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* ---- Toolbar ---- */
.fr-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 18px;
  padding: 14px 16px;
  margin-bottom: 18px;
  background: #f8f9fb;
  border: 1px solid #eef0f4;
  border-radius: 12px;
}
.fr-toolbar__group {
  display: flex;
  align-items: center;
  gap: 8px;
}
.fr-toolbar__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a0a5b0;
}
.fr-toolbar__divider {
  width: 1px;
  height: 26px;
  background: #e2e6ee;
}
.fr-year {
  width: 104px;
}
.fr-quarter {
  width: 210px;
}
.fr-region {
  width: 200px;
}
.fr-count {
  margin-left: auto;
  font-size: 13px;
  color: #909399;
}
.fr-count b {
  color: #1f2d3d;
}

/* ---- Stat cards ---- */
.fr-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.fr-stat {
  position: relative;
  overflow: hidden;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.fr-stat:hover {
  box-shadow: 0 8px 22px rgba(31, 45, 61, 0.09);
  transform: translateY(-2px);
}
.fr-stat::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}
.fr-stat--income::before { background: var(--fr-income); }
.fr-stat--outcome::before { background: var(--fr-outcome); }
.fr-stat--transfer::before { background: var(--fr-transfer); }
.fr-stat__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}
.fr-stat__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
.fr-stat--income .fr-stat__dot { background: var(--fr-income); }
.fr-stat--outcome .fr-stat__dot { background: var(--fr-outcome); }
.fr-stat--transfer .fr-stat__dot { background: var(--fr-transfer); }
.fr-stat__value {
  margin-top: 10px;
  font-size: 23px;
  font-weight: 700;
  color: #1f2d3d;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.fr-stat__full {
  margin-top: 4px;
  font-size: 12px;
  color: #a8abb2;
  font-variant-numeric: tabular-nums;
}

/* ---- Error ---- */
.fr-error {
  margin-bottom: 12px;
}
.fr-error__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

/* ---- Table ---- */
.fr-table {
  width: 100%;
  border-radius: 12px;
}
.fr-table :deep(thead th.el-table__cell) {
  background: #f5f7fa;
  color: #5a6473;
  font-weight: 600;
}
.fr-colhead {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.fr-colhead small {
  color: #a8abb2;
  font-weight: 500;
}
.fr-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.fr-dot--income { background: var(--fr-income); }
.fr-dot--outcome { background: var(--fr-outcome); }
.fr-dot--transfer { background: var(--fr-transfer); }
.fr-amount {
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: #1f2d3d;
}
.fr-hudud {
  font-weight: 500;
  color: #303133;
}
.fr-hudud--nested {
  padding-left: 14px;
  font-weight: 400;
  color: #606266;
}
.fr-row-actions {
  display: inline-flex;
  gap: 6px;
  justify-content: center;
}
.fr-act-icon {
  height: 14px;
  width: 14px;
}
/* Summary (totals) footer emphasis */
.fr-table :deep(.el-table__footer .el-table__cell) {
  background: #eef2f8;
  font-weight: 700;
  color: #1f2d3d;
  font-variant-numeric: tabular-nums;
}
</style>
